// src/pages/auth/ForgotPassword.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, ArrowLeft, CheckCircle, Zap } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import toast from 'react-hot-toast';

const resetPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const confirmResetSchema = z.object({
  confirmationCode: z.string().min(6, 'Confirmation code must be 6 digits'),
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, 
      'Password must contain uppercase, lowercase, number and special character'),
  confirmPassword: z.string(),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;
type ConfirmResetForm = z.infer<typeof confirmResetSchema>;

export const ForgotPassword: React.FC = () => {
  const { resetPassword, confirmResetPassword, isLoading, error, clearError } = useAuthStore();
  const [step, setStep] = useState<'email' | 'code' | 'success'>('email');
  const [email, setEmail] = useState('');

  const resetForm = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const confirmForm = useForm<ConfirmResetForm>({
    resolver: zodResolver(confirmResetSchema),
  });

  const handleResetRequest = async (data: ResetPasswordForm) => {
    try {
      clearError();
      await resetPassword(data.email);
      setEmail(data.email);
      setStep('code');
      toast.success('Reset code sent to your email!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send reset code');
    }
  };

  const handleResetConfirm = async (data: ConfirmResetForm) => {
    try {
      clearError();
      await confirmResetPassword(email, data.confirmationCode, data.newPassword);
      setStep('success');
      toast.success('Password reset successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset password');
    }
  };

  const renderEmailStep = () => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="w-full max-w-md mx-auto"
    >
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Forgot your password?
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Enter your email address and we'll send you a reset code.
        </p>
      </div>

      <form onSubmit={resetForm.handleSubmit(handleResetRequest)} className="space-y-6">
        <Input
          label="Email address"
          type="email"
          leftIcon={<Mail className="w-4 h-4" />}
          placeholder="Enter your email"
          error={resetForm.formState.errors.email?.message}
          {...resetForm.register('email')}
        />

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <Button
          type="submit"
          size="lg"
          className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
          loading={isLoading}
        >
          Send Reset Code
        </Button>

        <div className="text-center">
          <Link
            to="/auth/signin"
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-500 dark:text-primary-400"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to sign in</span>
          </Link>
        </div>
      </form>
    </motion.div>
  );

  const renderCodeStep = () => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="w-full max-w-md mx-auto"
    >
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Enter reset code
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          We've sent a 6-digit code to <strong>{email}</strong>
        </p>
      </div>

      <form onSubmit={confirmForm.handleSubmit(handleResetConfirm)} className="space-y-6">
        <Input
          label="Confirmation code"
          type="text"
          placeholder="Enter 6-digit code"
          className="text-center text-lg tracking-wider"
          maxLength={6}
          error={confirmForm.formState.errors.confirmationCode?.message}
          {...confirmForm.register('confirmationCode')}
        />

        <Input
          label="New password"
          type="password"
          leftIcon={<Lock className="w-4 h-4" />}
          placeholder="Enter new password"
          error={confirmForm.formState.errors.newPassword?.message}
          {...confirmForm.register('newPassword')}
        />

        <Input
          label="Confirm new password"
          type="password"
          leftIcon={<Lock className="w-4 h-4" />}
          placeholder="Confirm new password"
          error={confirmForm.formState.errors.confirmPassword?.message}
          {...confirmForm.register('confirmPassword')}
        />

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={isLoading}
        >
          Reset Password
        </Button>

        <div className="text-center space-y-2">
          <button
            type="button"
            onClick={() => handleResetRequest({ email })}
            className="text-sm text-primary-600 hover:text-primary-500 font-medium"
            disabled={isLoading}
          >
            Didn't receive the code? Resend
          </button>
          
          <br />
          
          <button
            type="button"
            onClick={() => setStep('email')}
            className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-500 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Use different email</span>
          </button>
        </div>
      </form>
    </motion.div>
  );

  const renderSuccessStep = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="w-full max-w-md mx-auto text-center"
    >
      <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
      </div>

      <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
        Password Reset Successful!
      </h2>
      
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Your password has been successfully reset. You can now sign in with your new password.
      </p>

      <Button
        size="lg"
        className="w-full bg-gradient-to-r from-primary-600 to-primary-700"
        onClick={() => window.location.href = '/auth/signin'}
      >
        Continue to Sign In
      </Button>
    </motion.div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Zap className="w-7 h-7" />
              </div>
              <h1 className="text-3xl font-bold">TubeOps</h1>
            </div>
            <h2 className="text-4xl font-bold mb-4">
              Reset your password securely
            </h2>
            <p className="text-xl text-primary-100 leading-relaxed">
              Don't worry, it happens to the best of us. Follow the simple steps to regain access to your account.
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-sm">1</span>
              </div>
              <span>Enter your email address</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-sm">2</span>
              </div>
              <span>Check your email for reset code</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-sm">3</span>
              </div>
              <span>Create a new secure password</span>
            </div>
          </div>
        </div>
        
        <div className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/10 rounded-full blur-xl" />
      </div>

      {/* Right Panel - Reset Form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-16 bg-white dark:bg-gray-900">
        {/* Mobile Logo */}
        <div className="lg:hidden mb-8">
          <div className="flex items-center space-x-3 justify-center">
            <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">TubeOps</h1>
          </div>
        </div>

        {step === 'email' && renderEmailStep()}
        {step === 'code' && renderCodeStep()}
        {step === 'success' && renderSuccessStep()}
      </div>
    </div>
  );
};

export default ForgotPassword;